import axios from 'axios';
import { getStoredToken, setStoredToken } from '../auth/AuthContext';

// Base API configuration
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const API_V1_PREFIX = '/api/v1';

// Create axios instance
const api = axios.create({
  baseURL: `${API_BASE_URL}${API_V1_PREFIX}`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Attach auth token (JWT) if present
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    // eslint-disable-next-line no-param-reassign
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// If we get a 401, clear token (simple MVP behavior)
api.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      setStoredToken(null);
    }
    return Promise.reject(err);
  }
);

// API service object
const apiService = {
  // ============================================
  // AUTH API
  // ============================================
  auth: {
    googleLogin: (idToken) => api.post('/auth/google', { id_token: idToken }),
    me: () => api.get('/auth/me'),
  },

  // ============================================
  // USERS API
  // ============================================
  users: {
    list: () => api.get('/users/'),
    get: (userId) => api.get(`/users/${userId}`),
    create: (userData) => api.post('/users/', userData),
    completeOnboarding: () => api.put('/users/me/onboarding-complete'),
    updateResearchOptedIn: (optedIn) => api.put('/users/me/research-opted-in', { opted_in: optedIn }),
  },

  // ============================================
  // WEARABLES API (JWT-only; no user_id in path)
  // ============================================
  wearables: {
    register: () => api.post('/wearables/register'),
    getProviders: (enabledOnly = true, cloudOnly = true) =>
      api.get('/wearables/providers', { params: { enabled_only: enabledOnly, cloud_only: cloudOnly } }),
    getConnections: () => api.get('/wearables/connections'),
    getSyncHistory: (provider = null, limit = 50) =>
      api.get('/wearables/sync-history', {
        params: {
          provider: provider || undefined,
          limit,
        },
      }),
    connectDevice: (provider, redirectUri = null) => {
      const params = redirectUri ? `?redirect_uri=${encodeURIComponent(redirectUri)}` : '';
      return api.get(`/wearables/authorize/${provider}${params}`);
    },
    syncData: (provider, dataType = 'all', { startDate, endDate, runAsync = false } = {}) =>
      api.post('/wearables/sync', {
        provider,
        data_type: dataType,
        start_date: startDate || null,
        end_date: endDate || null,
        run_async: runAsync,
      }),
    getTimeseries: (startTime, endTime, types = ['heart_rate'], limit = 100, resolution = 'raw', cursor = null) => {
      const query = new URLSearchParams();
      query.append('start_time', startTime);
      query.append('end_time', endTime);

      const effectiveTypes = Array.isArray(types) && types.length > 0 ? types : ['heart_rate'];
      effectiveTypes.forEach((type) => query.append('types', type));

      const cappedLimit = Math.min(Number(limit) || 100, 100);
      query.append('limit', String(cappedLimit));
      query.append('resolution', resolution || 'raw');
      if (cursor) query.append('cursor', cursor);

      return api.get(`/wearables/timeseries?${query.toString()}`);
    },
    getEventWorkouts: (startDate, endDate, workoutType = null, limit = 50, cursor = null) => {
      const params = { start_date: startDate, end_date: endDate, limit, cursor: cursor || undefined };
      if (workoutType) params.workout_type = workoutType;
      return api.get('/wearables/events/workouts', { params });
    },
    getSleepSessions: (startDate, endDate, limit = 50, cursor = null) =>
      api.get('/wearables/events/sleep', { params: { start_date: startDate, end_date: endDate, limit, cursor: cursor || undefined } }),
    getWorkouts: (limit = 50) => api.get('/wearables/workouts', { params: { limit } }),
    getActivitySummary: (startDate, endDate, limit = 50, cursor = null) =>
      api.get('/wearables/summaries/activity', { params: { start_date: startDate, end_date: endDate, limit, cursor: cursor || undefined } }),
    getSleepSummary: (startDate, endDate, limit = 50, cursor = null) =>
      api.get('/wearables/summaries/sleep', { params: { start_date: startDate, end_date: endDate, limit, cursor: cursor || undefined } }),
    getRecoverySummary: (startDate, endDate, limit = 50, cursor = null) =>
      api.get('/wearables/summaries/recovery', { params: { start_date: startDate, end_date: endDate, limit, cursor: cursor || undefined } }),
    getBodySummary: () => api.get('/wearables/summaries/body'),
    getSeriesTypes: () => api.get('/wearables/series-types'),
    getWorkoutDetail: (provider, workoutId) =>
      api.get(`/wearables/workouts/${provider}/${workoutId}`),
    importAppleHealth: (fileKey) =>
      api.post(`/wearables/import/apple-health?file_key=${encodeURIComponent(fileKey)}`),
    // Aggregated data for dashboard/legacy: fetches timeseries + workouts + sleep for a date range (no userId)
    getData: (days = null, startDate = null, endDate = null, types = null) => {
      const end = endDate || new Date().toISOString().slice(0, 10);
      const start = startDate || (days ? new Date(Date.now() - days * 86400000).toISOString().slice(0, 10) : new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
      const startTime = `${start}T00:00:00Z`;
      const endTime = `${end}T23:59:59Z`;
      const typesList = types && Array.isArray(types) ? types : ['heart_rate'];
      return Promise.all([
        (() => {
          const query = new URLSearchParams();
          query.append('start_time', startTime);
          query.append('end_time', endTime);
          typesList.forEach((type) => query.append('types', type));
          query.append('limit', '100');
          query.append('resolution', 'raw');
          return api.get(`/wearables/timeseries?${query.toString()}`);
        })().then((r) => r.data).catch(() => ({ data: [], count: 0 })),
        api.get('/wearables/events/workouts', { params: { start_date: start, end_date: end, limit: 100 } }).then((r) => r.data).catch(() => ({ data: [] })),
        api.get('/wearables/events/sleep', { params: { start_date: start, end_date: end, limit: 100 } }).then((r) => r.data).catch(() => ({ data: [] })),
      ]).then(([timeseriesRes, workoutsRes, sleepRes]) => ({
        data: {
          timeseries: timeseriesRes,
          workouts: workoutsRes,
          sleep: sleepRes,
        },
      }));
    },
  },


  // ============================================
  // LAB RESULTS API - Team Member 2
  // ============================================
  labResults: {
    getOverview: () => api.get('/lab-results/'),
    extractLabData: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/lab-results/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
    },
    uploadPDF: (file, userId) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`/lab-results/upload?user_id=${userId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
    },
    saveResults: (data) => api.post('/lab-results/save', data),
    updateResult: (resultId, data) => api.put(`/lab-results/results/${resultId}`, data),
    deleteResult: (resultId) => api.delete(`/lab-results/results/${resultId}`),
    getResults: () => api.get('/lab-results/results'),
    getResultDetail: (resultId) => api.get(`/lab-results/results/${resultId}`),
  },

  // ============================================
  // DEXA SCANS API
  // ============================================
  dexaScans: {
    extractData: (file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post('/dexa-scans/extract', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      });
    },
    saveResults: (data) => api.post('/dexa-scans/save', data),
    getResults: () => api.get('/dexa-scans/results'),
    getResultDetail: (resultId) => api.get(`/dexa-scans/results/${resultId}`),
  },

  // ============================================
  // QUESTIONNAIRES API - Team Member 3
  // ============================================
  questionnaires: {
    getOverview: () => api.get('/questionnaires/'),
    getTypes: () => api.get('/questionnaires/types'),
    saveDraft: (data) => api.put('/questionnaires/draft', data),
    getDraft: (type) => api.get(`/questionnaires/draft?questionnaire_type=${type}`),
  },
};

export default apiService;

